import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Ajout rapide des produits suggérés du panier (snippet cart-upsell).
 *
 * Même flux que le formulaire produit d'Horizon : POST sur /cart/add avec les
 * sections des cart-items-component présents, puis CartLinesUpdateEvent. Le
 * tiroir et la page panier re-rendent leur section avec la réponse (le produit
 * ajouté sort alors de la liste) et la pastille du panier se met à jour.
 */
class CartUpsellForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    event.preventDefault();
    const form = this.form;
    if (!form) return;

    const button = form.querySelector('button[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const config = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, { ...config, headers: { ...config.headers, Accept: 'text/html' } })
      .then((response) => response.json())
      .then(async (response) => {
        const cart = await fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        }).then((cartResponse) => cartResponse.json());

        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
        }

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'cart-upsell',
            itemCount: 1,
            sections: response.status ? undefined : response.sections,
            didError: Boolean(response.status),
          },
        });
      })
      .catch((error) => {
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => button?.removeAttribute('aria-busy'));
  };
}

if (!customElements.get('cart-upsell-form')) {
  customElements.define('cart-upsell-form', CartUpsellForm);
}
